import React from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const STYLE_ID = 'shop-custom-font-faces';

export const SYSTEM_FONT_OPTIONS = [
  { value: '', label: 'Standard (Theme)' },
  { value: 'Manrope', label: 'Manrope' },
  { value: 'Inter, system-ui, sans-serif', label: 'Inter' },
  { value: "'Playfair Display', Georgia, serif", label: 'Playfair Display' },
  { value: "Georgia, 'Times New Roman', serif", label: 'Georgia' },
  { value: "'Cormorant Garamond', Georgia, serif", label: 'Cormorant Garamond' },
  { value: "'Libre Baskerville', Georgia, serif", label: 'Libre Baskerville' },
  { value: "'DM Serif Display', Georgia, serif", label: 'DM Serif Display' },
  { value: "'Space Grotesk', system-ui, sans-serif", label: 'Space Grotesk' },
  { value: "'Josefin Sans', system-ui, sans-serif", label: 'Josefin Sans' },
  { value: 'Poppins, system-ui, sans-serif', label: 'Poppins' },
  { value: 'Montserrat, system-ui, sans-serif', label: 'Montserrat' },
  { value: "'JetBrains Mono', ui-monospace, monospace", label: 'JetBrains Mono' },
  { value: "'Bebas Neue', Impact, sans-serif", label: 'Bebas Neue' },
];

export const THEME_SYSTEM_FONTS = [
  'Manrope', 'Inter', 'Poppins', 'Montserrat', 'Fraunces', 'Playfair Display',
  'Cormorant Garamond', 'DM Serif Display', 'Libre Baskerville', 'Josefin Sans',
  'Space Grotesk', 'Bebas Neue',
];

let cachedFonts = null;
let pending = null;
const listeners = new Set();

const safeFamily = (value) => String(value || '').replace(/[^\w .-]/gu, '').trim();
const absoluteUrl = (url) => (String(url || '').startsWith('/') ? `${API}${url}` : url);

function installFontFaces(fonts) {
  if (typeof document === 'undefined') return;
  let style = document.getElementById(STYLE_ID);
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }
  style.textContent = (fonts || []).map((font) => {
    const family = safeFamily(font.family);
    const format = font.mime === 'font/woff' ? 'woff' : 'woff2';
    return `@font-face{font-family:"${family}";src:url("${absoluteUrl(font.url)}") format("${format}");font-weight:${font.weight || 400};font-style:${font.style || 'normal'};font-display:swap;}`;
  }).join('\n');
}

function publish(fonts) {
  cachedFonts = Array.isArray(fonts) ? fonts : [];
  installFontFaces(cachedFonts);
  listeners.forEach((listener) => listener(cachedFonts));
  return cachedFonts;
}

export async function loadCustomFonts(force = false) {
  if (cachedFonts && !force) return cachedFonts;
  if (pending && !force) return pending;
  pending = fetch(`${API}/api/shop/fonts`, { credentials: 'include' })
    .then((response) => {
      if (!response.ok) throw new Error('Schriftarten konnten nicht geladen werden');
      return response.json();
    })
    .then(publish)
    .finally(() => { pending = null; });
  return pending;
}

export function useCustomFonts() {
  const [fonts, setFonts] = React.useState(cachedFonts || []);
  const [loading, setLoading] = React.useState(!cachedFonts);

  React.useEffect(() => {
    listeners.add(setFonts);
    loadCustomFonts().catch(() => []).finally(() => setLoading(false));
    return () => listeners.delete(setFonts);
  }, []);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      return await loadCustomFonts(true);
    } finally {
      setLoading(false);
    }
  }, []);

  return { fonts, loading, refresh };
}

export function FontSelect({ value, onChange, testId, className = '', includeDefault = true }) {
  const { fonts } = useCustomFonts();
  const system = includeDefault ? SYSTEM_FONT_OPTIONS : SYSTEM_FONT_OPTIONS.filter((font) => font.value);
  return (
    <select value={value || ''} onChange={(event) => onChange(event.target.value)} className={className} data-testid={testId}>
      {system.map((font) => <option key={font.value || 'default'} value={font.value}>{font.label}</option>)}
      {fonts.length > 0 && <optgroup label="Eigene Schriftarten">
        {fonts.map((font) => (
          <option key={font.id} value={font.family}>{font.family} · {font.weight} · {font.style}</option>
        ))}
      </optgroup>}
    </select>
  );
}
